'use client';

import { useEffect, useState } from 'react';

import { FA } from '@/shared/components/ui/icon/FA';
import type { ChecklistAttrs, ChecklistItem } from './checklist.types';
import { safeParseItems } from './checklist.utils';
import s from './Checklist.module.scss';

interface Props {
  attrs: ChecklistAttrs;
}

export const Checklist = ({ attrs }: Props) => {
  const items = safeParseItems(attrs.items);
  const [checkedIndices, setCheckedIndices] = useState<number[]>([]);

  // Restore checked state from localStorage after mount (avoids hydration mismatch)
  useEffect(() => {
    if (!attrs.ls) return;
    try {
      const stored = JSON.parse(localStorage.getItem(attrs.ls) || '[]');
      if (Array.isArray(stored)) setCheckedIndices(stored);
    } catch {
      // ignore corrupt data
    }
  }, [attrs.ls]);

  const handleToggle = (index: number) => {
    const next = checkedIndices.includes(index)
      ? checkedIndices.filter((i) => i !== index)
      : [...checkedIndices, index];
    setCheckedIndices(next);
    if (attrs.ls) localStorage.setItem(attrs.ls, JSON.stringify(next));
  };

  if (!items.length) return null;

  return (
    <span className={s.wrapper}>
      <span className={s.header}>
        <FA icon="fa-list-check" theme="fad" className={s.headerIcon} />
        <span className={s.headerLabel}>Checklist</span>
        <span className={s.headerCounter}>
          {checkedIndices.length}/{items.length}
        </span>
      </span>
      {items.map((item: ChecklistItem, index: number) => {
        const isChecked = checkedIndices.includes(index);
        return (
          <span key={index} className={s.item} onClick={() => handleToggle(index)}>
            <label className={s.checkbox}>
              <input type="checkbox" checked={isChecked} readOnly className={s.input} />
              <span className={`${s.checkmark} ${isChecked ? s.checked : ''}`}>
                {isChecked && <FA icon="fa-check" theme="fas" className={s.checkIcon} />}
              </span>
            </label>
            <span className={s.content}>
              <span className={`${s.title} ${isChecked ? s.titleChecked : ''}`}>{item.title}</span>
              {!!item.description && (
                <span className={`${s.description} ${isChecked ? s.descriptionChecked : ''}`}>
                  {item.description}
                </span>
              )}
            </span>
          </span>
        );
      })}
    </span>
  );
};
